/**
 * Shared per-member task-stat helpers.
 *
 * Originally lived inside `pages/app/TeamPage.jsx` when the Team
 * surface was its own page. Lifted into this module when the
 * Members + Team pages were merged on 2026-06-08 — Members now
 * renders both a List view (the editable CRUD table) and a Grid
 * view (the task-signals cards), and both views' Grid path needs
 * the same aggregation primitives.
 *
 * No React imports here on purpose so this module stays trivially
 * testable and re-importable from any caller.
 */

import { dayOffsetFromToday } from './format.js'

/** Window (in days) used for the "Due Soon" stat column. Tasks whose
 *  due date falls between today (inclusive) and today + this many
 *  days (inclusive) count as "due soon". Set to 30 so the typical
 *  monthly planning horizon is covered — a task scheduled three weeks
 *  out shows up here, not just the ones due this week. */
export const DUE_SOON_DAYS = 30

/** A task counts as "open" when its workflow category is one of the
 *  pre-done buckets. The /admin/tasks payload exposes both the
 *  canonical `statusCategory` (backlog/active/review/done/cancelled)
 *  and the legacy four-bucket `status` (todo/in_progress/review/
 *  complete). We prefer the canonical one and fall back to the legacy
 *  bucket for rows from older responses. */
export function isTaskOpen(task) {
  const cat = task.statusCategory
  if (cat) return cat !== 'done' && cat !== 'cancelled'
  const bucket = task.status
  return bucket !== 'complete' && bucket !== 'cancelled'
}

/** A task counts as "To Do" (the FIRST workflow column — not started yet) by
 *  its canonical `statusCategory` (`backlog`), falling back to the legacy
 *  four-bucket `status` (`todo`). The "Open" stat counts To-Do-ONLY (subtasks
 *  included), matching the Team Overview "TO DO" card and the dashboards'
 *  "Open Tasks" rule — In Progress / Review no longer inflate it. */
export function isTaskTodo(task) {
  const cat = task.statusCategory
  if (cat) return cat === 'backlog'
  return task.status === 'todo'
}

/** Is this task past its due date RIGHT NOW?
 *
 *  The single client-side definition of "overdue", matching the server's
 *  `isPastDue` (flowdesk-api/src/utils/task-overdue.js):
 *    • an explicit due TIME is overdue the moment that instant passes;
 *    • a date-only due is overdue only from the NEXT calendar day, so it
 *      never flips early just because of the viewer's UTC offset.
 *
 *  Extracted because the Space list's Overdue filter carried its own
 *  day-offset-only copy of this and therefore disagreed with the stats and
 *  the API for any task with a time (defect DF_013 / Mantis 22120). Both call
 *  this now, so the two cannot drift apart again. `dayOffset` is passed in by
 *  callers that already computed it; `now` is injectable for tests.
 *
 *  Note this answers "is it past due", NOT "should it be shown as overdue" —
 *  callers apply their own open/done check. */
export function isPastDueNow(task, dayOffset, now = Date.now()) {
  if (!task?.dueDate) return false
  if (task.dueDateHasTime) return new Date(task.dueDate).getTime() < now
  return dayOffset != null && dayOffset < 0
}

/** Parse a `YYYY-MM-DD` string as local midnight, so "due today"
 *  doesn't flip to "overdue" depending on the server's UTC offset. */
export function parseDueDate(d) {
  if (!d) return null
  const parts = String(d).split('-')
  if (parts.length !== 3) return null
  const [y, m, day] = parts.map((n) => Number(n))
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(day)) {
    return null
  }
  return new Date(y, m - 1, day)
}

/** Format a Date as `YYYY-MM-DD` in the local timezone. Used to
 *  build the `dueFrom` / `dueTo` query params Task Monitor consumes
 *  — the backend compares those against `task.dueDate` (also a
 *  YYYY-MM-DD slice), so a local-midnight string keeps the
 *  comparison stable across timezones. */
export function toIsoDateLocal(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Monday 00:00 of the current week, local time. Used by the
 *  "Completed this week" stat so a task that closed on Saturday
 *  10pm still counts when an admin opens the page on Monday
 *  morning. */
export function startOfThisWeek() {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  // Distance back to Monday: Mon(1)→0, Tue(2)→1, …, Sun(0)→6.
  const day = d.getDay()
  const distance = (day + 6) % 7
  d.setDate(d.getDate() - distance)
  return d
}

/** Build a Task Monitor URL pre-filtered to a single assignee + bucket.
 *  Task Monitor matches assignees by `fullName` (string) on the client
 *  side, so we pass the name directly. The bucket maps to one of:
 *    'open'              → exclude done + cancelled
 *    'dueSoon'           → open + dueFrom=today, dueTo=today+N days
 *    'overdue'           → friendly alias the page expands to
 *                          dueTo=yesterday + exclude done/cancelled
 *    'completed'         → statusCategory=done — every DELIVERED (done/
 *                          closed) task, ALL-TIME, so the destination
 *                          list matches the all-time "Done" count in the
 *                          Members table (a task completed in a prior
 *                          week must still reflect in the member's record).
 *                          [legacy note] an earlier version scoped this to
 *                          this-week (completedWeek=1) —
 *                          the card said N while the page showed a
 *                          different set, read as "completion count
 *                          not updated" (QA-reported).
 */
export function buildTaskMonitorLink(fullName, bucket) {
  if (!fullName) return '/task-monitor'
  const params = new URLSearchParams()
  params.set('assignee', fullName)
  if (bucket === 'open') {
    params.set('statusCategoryExclude', 'done,cancelled')
  } else if (bucket === 'dueSoon') {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const cutoff = new Date(today)
    cutoff.setDate(today.getDate() + DUE_SOON_DAYS)
    params.set('statusCategoryExclude', 'done,cancelled')
    params.set('dueFrom', toIsoDateLocal(today))
    params.set('dueTo', toIsoDateLocal(cutoff))
  } else if (bucket === 'overdue') {
    params.set('filter', 'overdue')
  } else if (bucket === 'completed') {
    // All-time completed → the Task Monitor's done-category filter (not the
    // this-week window), matching the all-time count in the "Done" column.
    params.set('statusCategory', 'done')
  }
  return `/task-monitor?${params.toString()}`
}

/** Aggregate per-member stats from the flat /admin/tasks payload.
 *  Returns a Map keyed by userId →
 *    { open, dueSoon, overdue, completed }  (completed = all-time delivered).
 *  A task with N assignees contributes 1 to each of those N counters
 *  (matches how Task Monitor's filter chip behaves when multiple
 *  assignees share a row). The loop accepts both open tasks AND
 *  tasks that closed this week so a fast contributor who has cleared
 *  their queue still surfaces in the map (without the completed
 *  branch, they'd show "No active tasks" — a stale read of "they're
 *  idle"). */
export function buildStatsByUser(tasks) {
  const stats = new Map()

  // Defensive: a transient non-array payload (mid-refetch, an unexpected
  // API shape, a cache hiccup) must not throw "tasks is not iterable" and
  // white-screen the whole Members page via the top-level ErrorBoundary.
  const list = Array.isArray(tasks) ? tasks : []
  for (const t of list) {
    const notDone = isTaskOpen(t)
    // "Open" = TO DO (first column) only, subtasks included. In Progress /
    // Review are still not-done (so they feed overdue / due-soon below) but no
    // longer count as "Open" — matching the Team Overview / dashboard rule.
    const isTodo = notDone && isTaskTodo(t)
    // Delivered = done/closed only. Cancelled tasks ALSO carry a
    // completedAt stamp (every terminal transition stamps it), so a
    // raw completedAt test counted abandoned work as "completed" —
    // and diverged from the Task Monitor's completedWeek filter and
    // the admin dashboard card, which both count delivered work only.
    const cat = t.statusCategory
    const delivered = cat
      ? cat === 'done' || cat === 'closed'
      : t.status === 'complete'
    // Completed = delivered (done/closed) at ANY time — the member's TOTAL
    // completed count, not just this week. A task completed in a prior week
    // must still reflect in their record (reported 2026-07-30: a completed
    // task never showed in the "Done" column). Cancelled work carries a
    // completedAt stamp but its category isn't done/closed, so it's excluded.
    if (!notDone && !delivered) continue

    // Overdue / due-soon.
    //   • Task WITH an explicit due time → the deadline is that exact
    //     instant, so it's overdue the moment it passes (a task due today
    //     12:30 PM is overdue at 12:31 — the previous calendar-day-only
    //     check left it "due soon" until midnight, so a same-day expired
    //     task never counted as overdue).
    //   • DATE-ONLY due date → compare by CALENDAR DAY in the tenant
    //     display timezone (overdue only once the whole day has passed),
    //     so a date-only "due today" isn't flipped early or shifted across
    //     the UTC boundary.
    // dueSoon excludes overdue so a task moves cleanly from one to the other.
    const dueOffset = t.dueDate ? dayOffsetFromToday(t.dueDate) : null
    const isOverdue = notDone && isPastDueNow(t, dueOffset)
    const isDueSoon =
      notDone &&
      !isOverdue &&
      dueOffset != null &&
      dueOffset >= 0 &&
      dueOffset <= DUE_SOON_DAYS

    const assignees = Array.isArray(t.assignees) ? t.assignees : []
    if (assignees.length === 0) continue
    for (const a of assignees) {
      const uid = a?.id
      if (!uid) continue
      const cur =
        stats.get(uid) ?? {
          open: 0,
          dueSoon: 0,
          overdue: 0,
          completed: 0,
        }
      if (notDone) {
        if (isTodo) cur.open += 1
        if (isDueSoon) cur.dueSoon += 1
        if (isOverdue) cur.overdue += 1
      }
      if (delivered) {
        cur.completed += 1
      }
      stats.set(uid, cur)
    }
  }
  return stats
}

/** Convert a `lastActiveAt` ISO timestamp into a short caption +
 *  inactivity flag for the card header. Returns:
 *    - null when the user has never been active (caller renders nothing)
 *    - { label, isStale } otherwise
 *
 *  `isStale` is true when the user hasn't been active for >= 14 days.
 *  The card uses it to paint the line in the muted-warning tone so a
 *  Company Admin can spot a checked-out team member at a scroll.
 *
 *  14 days is the threshold the rest of the app (Workload Balance
 *  "stale 14d+" counter, the SubAdminDashboard's inactive flag) also
 *  uses, so the visual language stays consistent.
 *
 *  Caller is expected to format the relative-time half via
 *  `fmtRelative` from `lib/dashboard-helpers` — keeping the
 *  formatter out of this module preserves the no-React-imports
 *  promise. */
export function buildLastActiveBadge(lastActiveAt, fmtRelative) {
  if (!lastActiveAt) return null
  const d = new Date(lastActiveAt)
  if (Number.isNaN(d.getTime())) return null
  const STALE_MS = 14 * 24 * 60 * 60 * 1000
  const isStale = Date.now() - d.getTime() >= STALE_MS
  return {
    label: `Last active ${fmtRelative(lastActiveAt)}`,
    isStale,
  }
}

/** Tier slug used for filter-chip matching and the role pill on a
 *  member card. Company Admins (tenant owner) get their own
 *  classification so they stay visually distinct from the
 *  delegated Sub Admins. */
export function classifyMemberTier(member) {
  if (member.role === 'company_admin') return 'owner'
  if (member.role === 'sub_admin') return 'sub_admin'
  return member.workRole || 'employee'
}

/** Role tier → shared RolePill label + tone. Mirrors the All Users
 *  page and the user detail modal so the visual language stays
 *  consistent across every surface that shows a member's tier. */
export const ROLE_PILL_FOR_TIER = {
  owner: { label: 'Owner', tone: 'amber' },
  sub_admin: { label: 'Sub Admin', tone: 'accent' },
  manager: { label: 'Manager', tone: 'info' },
  team_leader: { label: 'Team Lead', tone: 'success' },
  employee: { label: 'Employee', tone: 'muted' },
}
